
import { getCommonPrintStyles, getHeaderHtml, getFooterHtml, getSignatureHtml } from "./printStyles";
import { downloadAsJPG, downloadAsPDF, createPrintDocument } from "./printingBridge";
import { toast } from "sonner";
import { Admin } from '@/types/admin.types';
import { format } from 'date-fns';

const RECORDS_PER_PAGE = 12; // Set to 12 as requested

/**
 * Export admin list as JPG with pagination support
 */
export const printAdminsListPaginatedAsJPG = async (
  admins: Admin[],
  startPage: number = 1,
  endPage: number = 1
) => {
  try {
    console.log(`Starting Admins List JPG download...`, { admins: admins.length, startPage, endPage });
    
    const loadingToast = toast.loading(`Generating Admins List JPG...`);
    
    const { content, filename } = generateAdminsListContent(admins, startPage, endPage);
    
    console.log('Starting JPG download with filename:', filename);
    
    await downloadAsJPG(content, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error(`Failed to download Admins List`);
      }
    });
    
    toast.dismiss(loadingToast);
    const pageText = startPage === endPage ? `page ${startPage}` : `pages ${startPage}-${endPage}`;
    toast.success(`Admins List ${pageText} downloaded as JPG!`);
    
  } catch (error) {
    console.error(`Error downloading paginated Admins List:`, error);
    toast.error(`Failed to download Admins List`);
  }
};

/**
 * Export admin list as PDF with pagination support
 */
export const printAdminsListPaginatedAsPDF = async (
  admins: Admin[],
  startPage: number = 1,
  endPage: number = 1
) => {
  try {
    console.log(`Starting Admins List PDF download...`, { admins: admins.length, startPage, endPage });
    
    const loadingToast = toast.loading(`Generating Admins List PDF...`);
    
    const { content, filename } = generateAdminsListContent(admins, startPage, endPage);
    
    console.log('Starting PDF download with filename:', filename);
    
    await downloadAsPDF(content, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error(`Failed to download Admins List`);
      }
    });
    
    toast.dismiss(loadingToast);
    const pageText = startPage === endPage ? `page ${startPage}` : `pages ${startPage}-${endPage}`;
    toast.success(`Admins List ${pageText} downloaded as PDF!`);
    
  } catch (error) {
    console.error(`Error downloading paginated Admins List:`, error);
    toast.error(`Failed to download Admins List`);
  }
};

const generateAdminsListContent = (admins: Admin[], startPage: number, endPage: number) => {
  const totalPages = Math.ceil(admins.length / RECORDS_PER_PAGE);
  const actualEndPage = Math.min(endPage, totalPages);
  const actualStartPage = Math.max(1, startPage);
  
  console.log('Calculated pages:', { totalPages, actualStartPage, actualEndPage });
  
  // Calculate which records to include
  const startIndex = (actualStartPage - 1) * RECORDS_PER_PAGE;
  const endIndex = actualEndPage * RECORDS_PER_PAGE;
  const adminsToShow = admins.slice(startIndex, endIndex);
  
  console.log('Admins to show:', { startIndex, endIndex, count: adminsToShow.length });
  
  // Group records by pages
  const pages: Admin[][] = [];
  for (let i = 0; i < adminsToShow.length; i += RECORDS_PER_PAGE) {
    pages.push(adminsToShow.slice(i, i + RECORDS_PER_PAGE));
  }
  
  // Generate content for all pages using standardized structure
  const allPagesContent = pages.map((pageAdmins, pageIndex) => {
    const currentPageNumber = actualStartPage + pageIndex;
    
    return `
      <div class="print-page ${pageIndex > 0 ? 'page-break' : ''}">
        ${getHeaderHtml(`Administrators List - Page ${currentPageNumber} of ${totalPages}`)}
        
        <div class="print-content">
          <div class="page-info">
            <strong>Page ${currentPageNumber} of ${totalPages}</strong> | 
            Records ${(currentPageNumber - 1) * RECORDS_PER_PAGE + 1} - ${Math.min(currentPageNumber * RECORDS_PER_PAGE, admins.length)} of ${admins.length}
          </div>
          
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Position</th>
                <th>Role</th>
                <th>Email</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${pageAdmins.map((admin, index) => {
                const displayId = (startIndex + (pageIndex * RECORDS_PER_PAGE) + index + 1).toString();
                
                return `
                  <tr>
                    <td>${displayId}</td>
                    <td>${admin.firstName} ${admin.lastName}</td>
                    <td>${admin.position || 'N/A'}</td>
                    <td>${admin.role}</td>
                    <td>${admin.email}</td>
                    <td>${admin.status}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          ${pageIndex === pages.length - 1 ? `
            <div class="summary">
              Total Administrators: ${admins.length} | Downloaded Pages: ${pages.length}/${totalPages}
            </div>
          ` : ''}
          
          ${getSignatureHtml('admin')}
        </div>
        
        ${getFooterHtml('admin')}
      </div>
    `;
  }).join('');

  const styles = `
    ${getCommonPrintStyles()}
    .page-info {
      text-align: center;
      font-size: 0.9em;
      color: #666;
      margin: 10px 0;
      padding: 8px;
      background: #f8f9fa;
      border-radius: 4px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 16px;
      font-size: 0.85em;
    }
    th, td {
      padding: 8px 6px;
      border-bottom: 1px solid #ddd;
      text-align: left;
      vertical-align: top;
    }
    th {
      background: #f5f5f5;
      font-weight: bold;
      border-bottom: 2px solid #ccc;
      font-size: 0.9em;
    }
    .summary {
      margin-top: 20px;
      font-weight: bold;
      font-size: 1.1em;
      text-align: center;
      padding: 10px;
      background: #f8f9fa;
      border-radius: 4px;
    }
  `;

  // Generate the complete print document
  const printDocument = createPrintDocument(allPagesContent, `Administrators_List_Paginated`, styles);
  
  // Create filename
  const today = format(new Date(), 'yyyy-MM-dd');
  const filename = actualStartPage === actualEndPage 
    ? `Administrators_List_Page_${actualStartPage}_${today}`
    : `Administrators_List_Pages_${actualStartPage}-${actualEndPage}_${today}`;
  
  return { content: printDocument, filename };
};
